/**
 * MontaTech v2 - useMontaTechState Hook
 *
 * Hook principal que coordena todo o estado do MontaTech
 * Fluxo unico: C# nativo (coleta + apply)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { sendToTerminal } from '../../../../lib/terminalLogger';
import type {
  MontaTechStatus,
  MontaTechCurveData,
  SettingsTab
} from '../types';
import { collectShapesNative } from '../services/nativeCollector';
import { applyNestingNative } from '../services/nativeApply';
import {
  calculateNesting as calculateNestingApi,
  optimizeNesting,
  cancelOptimize,
  RFigure,
  MaterialResult,
  buildCsharpApplyFiguresFromResult,
  closeSession,
  stopCalculation as stopCalculationApi,
  type OptimizeLevel,
  OPTIMIZE_ATTEMPTS
} from '../services/nestingCalculator';
import { auditNestingBounds, type NestingBoundsWarning } from '../services/nestingBoundsAudit';
import { useProfiles } from './useProfiles';
import { useCanvas } from './useCanvas';
import { formatLength, getStoredDisplayUnit } from '../../../../lib/units';

export interface UseMontaTechStateReturn {
  // Status
  status: MontaTechStatus;
  error: string | null;
  progress: number; // 0-100
  progressLabel: string | null;
  isRecovering: boolean; // banner "reconectando" durante recuperação de conexão zumbi
  emptyState: {
    title: string;
    description: string;
    actionLabel: string;
  } | null;

  // Data
  curveData: MontaTechCurveData | null;
  nestingResult: RFigure[] | null;
  maximumY: number | null;
  boundsWarning: NestingBoundsWarning | null;
  toolVersion: number | null;

  // Canvas
  containerRef: React.RefObject<HTMLDivElement>;
  canvas: ReturnType<typeof useCanvas>;

  // Profiles
  profiles: ReturnType<typeof useProfiles>;

  // Settings Modal
  showSettings: boolean;
  settingsTab: SettingsTab;
  setShowSettings: (show: boolean) => void;
  setSettingsTab: (tab: SettingsTab) => void;
  
  // ✅ Layout de Grid (pode ser alterado antes de aplicar)
  applyBlocksPerRow: number;
  setApplyBlocksPerRow: (value: number) => void;

  // Actions
  collectShapes: () => void;
  calculateNesting: () => void;
  optimizeResult: (level: OptimizeLevel) => void;
  applyToCorelDraw: () => void;
  cancelCalculation: () => void;
  reset: () => void;

  // Computed
  isProcessing: boolean;
  canCollect: boolean;
  canCalculate: boolean;
  canOptimize: boolean;
  canApply: boolean;
  canCancel: boolean;
}



interface UseMontaTechStateOptions {
  onApplySuccess?: () => void;
}

export function useMontaTechState(options?: UseMontaTechStateOptions): UseMontaTechStateReturn {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const currentCalcRequestIdRef = useRef<string | null>(null);
  const autoCollectStartedRef = useRef(false);
  const lastResultTraceIdRef = useRef<string | null>(null);
  const lastResultStateSetAtPerfRef = useRef<number | null>(null);

  // Core state
  const [status, setStatus] = useState<MontaTechStatus>('collecting');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState<string | null>('Coletando seleção atual...');
  // Banner "Conexão instável, reconectando...". Estado próprio (NÃO progressLabel,
  // que o simTimer do cálculo sobrescreve a cada 300ms).
  const [isRecovering, setIsRecovering] = useState(false);
  const [emptyState, setEmptyState] = useState<UseMontaTechStateReturn['emptyState']>(null);
  const [curveData, setCurveData] = useState<MontaTechCurveData | null>(null);
  const [nestingResult, setNestingResult] = useState<RFigure[] | null>(null);
  const [materials, setMaterials] = useState<MaterialResult[] | null>(null);
  const [maximumY, setMaximumY] = useState<number | null>(null);
  const [boundsWarning, setBoundsWarning] = useState<NestingBoundsWarning | null>(null);
  const lastCalcResponseRef = useRef<any>(null);

  // Settings modal state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('profile');

  // Profiles hook
  const profiles = useProfiles();
  
  // ✅ Estado para blocksPerRow usado na aplicação (pode ser alterado pelo usuário)
  const [applyBlocksPerRow, setApplyBlocksPerRow] = useState<number>(3);

  const resolveBoundsWarning = useCallback((
    materialsValue: MaterialResult[] | null,
    maximumYValue: number | null,
    plotcalcErrors?: boolean,
    unplacedCount?: number,
  ): NestingBoundsWarning | null => {
    return auditNestingBounds({
      profile: profiles.editingProfile,
      materials: materialsValue,
      maximumY: maximumYValue,
      plotcalcErrors,
      unplacedCount,
      curveData,
    });
  }, [profiles.editingProfile, curveData]);

  // Canvas hook
  const canvas = useCanvas({
    curveData,
    nestingResult,
    materials,
    maximumY,
    profile: profiles.editingProfile,
    containerRef,
    blocksPerRow: applyBlocksPerRow,  // ✅ Passar o valor que pode ser alterado pelo usuário
    renderTraceId: lastResultTraceIdRef.current,
    renderTraceStateSetAtPerf: lastResultStateSetAtPerfRef.current
  });

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // ✅ Sincronizar applyBlocksPerRow quando o perfil mudar (mas não quando o usuário mudar manualmente)
  useEffect(() => {
    // Só atualiza se não houver resultado carregado (evita sobrescrever edição do usuário)
    if (!nestingResult && !lastCalcResponseRef.current) {
      setApplyBlocksPerRow(profiles.editingProfile.blocksPerRow || 3);
    }
  }, [profiles.editingProfile?.blocksPerRow, nestingResult]);
  
  // ✅ Sincronizar lastCalcResponseRef quando applyBlocksPerRow mudar
  useEffect(() => {
    if (lastCalcResponseRef.current?.data?.figures) {
      lastCalcResponseRef.current.data.blocksPerRow = applyBlocksPerRow;
      if (lastCalcResponseRef.current.profile) {
        lastCalcResponseRef.current.profile.blocksPerRow = applyBlocksPerRow;
      }
    }
  }, [applyBlocksPerRow]);

  const emptySelectionState = {
    title: 'Nenhum objeto selecionado no CorelDRAW',
    description: 'Selecione os objetos que deseja montar e tente novamente.',
    actionLabel: 'Coletar novamente'
  } as const;

  // ==================== ACTIONS ====================

  /**
   * Coleta shapes do CorelDRAW via C# nativo
   */
  const handleCollectShapes = useCallback(async (mode: 'auto' | 'manual' = 'manual') => {
    const collectStartTime = Date.now();
    const hasExistingPreview = (!!curveData && curveData.count > 0) || (!!nestingResult && nestingResult.length > 0) || !!lastCalcResponseRef.current;
    const shouldPreservePreview = mode === 'manual' && hasExistingPreview;

    if (mode === 'manual' && hasExistingPreview) {
      const confirmed = window.confirm('Isso vai substituir o preview atual pela seleção atual do CorelDRAW. Deseja continuar?');
      if (!confirmed) {
        return;
      }
    }

    setStatus('collecting');
    setError(null);
    setProgress(0);
    setProgressLabel(mode === 'auto' ? 'Coletando seleção atual...' : 'Coletando nova seleção...');
    setIsRecovering(false);
    setEmptyState(null);

    if (!shouldPreservePreview) {
      setCurveData(null);
      setNestingResult(null);
      setMaterials(null);
      setMaximumY(null);
      lastCalcResponseRef.current = null;
      requestIdRef.current = null;
    }

    try {
      // Simular progresso durante a coleta (C# nao envia progress updates)
      const progressTimer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 50);

      setProgressLabel('Coletando shapes via COM nativo...');

      const result = await collectShapesNative();
      clearInterval(progressTimer);

      const elapsed = Date.now() - collectStartTime;

      if (!isMountedRef.current) return;

      if (result.success && result.data) {
        if (result.data.count > 0) {
          if (requestIdRef.current) {
            closeSession(requestIdRef.current).catch(() => { });
            requestIdRef.current = null;
          }

          setProgress(100);
          setProgressLabel('Coleta concluida!');
          setCurveData(result.data);
          setNestingResult(null);
          setMaterials(null);
          setMaximumY(null);
          lastCalcResponseRef.current = null;
          setStatus('ready');
          setError(null);
        } else if (shouldPreservePreview) {
          setProgress(0);
          setProgressLabel(null);
          setStatus('ready');
          setError(emptySelectionState.title);
        } else {
          setProgress(0);
          setProgressLabel(null);
          setStatus('ready');
          setCurveData(null);
          setNestingResult(null);
          setMaterials(null);
          setMaximumY(null);
          lastCalcResponseRef.current = null;
          setError(null);
          setEmptyState(emptySelectionState);
        }

      } else {
        setProgress(0);
        setProgressLabel(null);
        setStatus(shouldPreservePreview ? 'ready' : 'error');
        setError(result.error || 'Erro na coleta nativa');

        sendToTerminal('[MONTATECH] COLETA FALHOU', {
          error: result.error,
          elapsedMs: elapsed
        });
      }
    } catch (err) {
      const elapsed = Date.now() - collectStartTime;
      if (!isMountedRef.current) return;

      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      setProgress(0);
      setProgressLabel(null);
      setStatus(shouldPreservePreview ? 'ready' : 'error');
      setError(errorMsg);

      sendToTerminal('[MONTATECH] EXCECAO NA COLETA', {
        error: errorMsg,
        elapsedMs: elapsed
      });
    }
  }, [curveData, nestingResult]);

  /**
   * Callback de progresso do calculo (WebSocket)
   */
  const lastProgressRef = useRef(0);
  const handleProgress = useCallback((percent: number) => {
    if (!isMountedRef.current) return;
    const clamped = Math.min(Math.max(0, percent), 99);
    // Nunca diminuir a barra de progresso
    if (clamped >= lastProgressRef.current) {
      lastProgressRef.current = clamped;
      setProgress(clamped);
      setProgressLabel(`Calculando encaixe... ${Math.round(clamped)}%`);
    }
  }, []);

  /**
   * Calcula o nesting via API
   */
  const handleCalculateNesting = useCallback(async () => {
    if (!curveData) {
      setError('Nenhum dado coletado. Execute a coleta primeiro.');
      return;
    }

    const T0 = performance.now(); // ⏱️ TIMESTAMP GLOBAL — clique no botão "Calcular"
    sendToTerminal(`⏱️ [T+0ms] Botão "Calcular" clicado`);

    lastProgressRef.current = 0; // Reset progress tracker
    setStatus('calculating');
    setError(null);
    setProgress(0);
    setProgressLabel('Enviando dados para o servidor...');
    setIsRecovering(false);
    setNestingResult(null);
    setMaterials(null);
    setMaximumY(null);
    setBoundsWarning(null);

    const calcRequestId = `montatech-${Date.now()}`;
    currentCalcRequestIdRef.current = calcRequestId;

    sendToTerminal(`⏱️ [T+${Math.round(performance.now() - T0)}ms] State resetado, chamando calculateNestingApi...`);

    // Progresso híbrido: simulação como base + progress real quando disponível
    // A simulação sobe suavemente 0→90% em ~30s. Se o backend envia progress real
    // e está à frente, usa o real. Nunca desce.
    let simProgress = 5;
    let realProgress = 0;
    const simTimer = setInterval(() => {
      simProgress = Math.min(simProgress + 2, 90);
      const effective = Math.max(simProgress, realProgress);
      lastProgressRef.current = effective;
      setProgress(effective);
      if (effective < 30) {
        setProgressLabel('Enviando dados para o servidor...');
      } else if (effective < 60) {
        setProgressLabel('Calculando encaixe...');
      } else if (effective < 90) {
        setProgressLabel('Otimizando posições...');
      } else {
        setProgressLabel('Finalizando...');
      }
    }, 300);

    const wrappedProgress = (percent: number) => {
      // Guardar o progress real — o simTimer usa o max(sim, real)
      if (percent > realProgress) {
        realProgress = percent;
        // Se o real está à frente da simulação, atualizar imediatamente
        if (percent > simProgress) {
          lastProgressRef.current = percent;
          setProgress(percent);
          setProgressLabel(`Calculando encaixe... ${percent}%`);
        }
      }
    };

    try {
      const tBeforeCalc = performance.now();
      const result = await calculateNestingApi({
        curveData,
        profile: profiles.editingProfile,
        requestId: calcRequestId,
        onProgress: wrappedProgress,
        onRecovering: () => { if (isMountedRef.current) setIsRecovering(true); },
        onRecovered: () => { if (isMountedRef.current) setIsRecovering(false); }
      });

      const tAfterCalc = performance.now();
      sendToTerminal(`⏱️ [T+${Math.round(tAfterCalc - T0)}ms] calculateNestingApi retornou (await durou ${Math.round(tAfterCalc - tBeforeCalc)}ms)`);
      sendToTerminal('📥 [MONTATECH/STATE] Await do calculateNestingApi retornou', {
        requestId: result.requestId || null,
        frontendResultReceivedAtMs: result.meta?.frontendResultReceivedAtMs ?? null,
        backendEmittedAtMs: result.meta?.backendEmittedAtMs ?? null,
        backendToFrontendMs:
          typeof result.meta?.backendEmittedAtMs === 'number' && typeof result.meta?.frontendResultReceivedAtMs === 'number'
            ? result.meta.frontendResultReceivedAtMs - result.meta.backendEmittedAtMs
            : null,
        frontendHandleResultMs:
          typeof result.meta?.frontendResultReceivedAtPerf === 'number' && typeof result.meta?.frontendResolveAtPerf === 'number'
            ? Math.round(result.meta.frontendResolveAtPerf - result.meta.frontendResultReceivedAtPerf)
            : null
      });

      clearInterval(simTimer);

      if (!isMountedRef.current) return;

      currentCalcRequestIdRef.current = null;
      setIsRecovering(false);

      if (result.success && (result.figures || result.materials)) {
        const tPipeline = performance.now();
        sendToTerminal(`⏱️ [T+${Math.round(tPipeline - T0)}ms] Resultado success=true, iniciando pipeline...`);
        requestIdRef.current = result.requestId || null;
        lastProgressRef.current = 95;
        setProgress(95);
        setProgressLabel('Renderizando resultado...');

        sendToTerminal(`⏱️ [T+${Math.round(tPipeline - T0)}ms] PIPELINE 1/5 — Aguardando UI yield (50ms)...`);
        // Liberar o event loop para o React atualizar a UI antes do processamento pesado
        await new Promise(r => setTimeout(r, 50));

        const t1 = performance.now();
        sendToTerminal(`⏱️ [T+${Math.round(t1 - T0)}ms] PIPELINE 2/5 — UI yield concluído, setando state React...`);

        setNestingResult(result.figures || null);
        setMaterials(result.materials || null);
        setMaximumY(result.maximumY || null);

        const nextBoundsWarning = resolveBoundsWarning(
          result.materials || null,
          result.maximumY || null,
          result.errors,
          result.meta?.unplacedCount,
        );
        setBoundsWarning(nextBoundsWarning);
        if (nextBoundsWarning) {
          sendToTerminal('⚠️ [MONTATECH/BOUNDS] Resultado acima do perfil configurado', {
            summary: nextBoundsWarning.summary,
            details: nextBoundsWarning.details,
            configuredHeightMm: nextBoundsWarning.configuredMaterialHeightMm,
            maximumY_mm: nextBoundsWarning.maximumY_mm,
          });
        }

        setProgress(100);
        setProgressLabel('Concluido!');
        setStatus('ready');
        lastResultTraceIdRef.current = result.requestId || null;
        lastResultStateSetAtPerfRef.current = performance.now();

        const t2 = performance.now();
        sendToTerminal(`⏱️ [T+${Math.round(t2 - T0)}ms] PIPELINE 3/5 — State setado (${Math.round(t2 - t1)}ms), preparando C#...`);
        sendToTerminal('🧠 [MONTATECH/STATE] State React atualizado para preview', {
          requestId: result.requestId || null,
          stateSetAtPerf: Math.round(lastResultStateSetAtPerfRef.current),
          elapsedFromResolveMs:
            typeof result.meta?.frontendResolveAtPerf === 'number'
              ? Math.round(lastResultStateSetAtPerfRef.current - result.meta.frontendResolveAtPerf)
              : null
        });
        requestAnimationFrame(() => {
          const raf1Perf = performance.now();
          sendToTerminal('🖼️ [MONTATECH/STATE] requestAnimationFrame #1 após setState', {
            requestId: result.requestId || null,
            elapsedFromStateSetMs:
              lastResultStateSetAtPerfRef.current != null
                ? Math.round(raf1Perf - lastResultStateSetAtPerfRef.current)
                : null,
          });
          requestAnimationFrame(() => {
            const raf2Perf = performance.now();
            sendToTerminal('🖼️ [MONTATECH/STATE] requestAnimationFrame #2 após setState', {
              requestId: result.requestId || null,
              elapsedFromStateSetMs:
                lastResultStateSetAtPerfRef.current != null
                  ? Math.round(raf2Perf - lastResultStateSetAtPerfRef.current)
                  : null,
              elapsedFromRaf1Ms: Math.round(raf2Perf - raf1Perf),
            });
          });
        });

        // ✅ CORREÇÃO: Guardar resposta no formato C# para apply
        const blocksPerRow = applyBlocksPerRow;

        const figuresForCsharp = buildCsharpApplyFiguresFromResult(result);

        const t3 = performance.now();
        sendToTerminal(`⏱️ [T+${Math.round(t3 - T0)}ms] PIPELINE 4/5 — Payload C# (${figuresForCsharp.length} figs) em ${Math.round(t3 - t2)}ms, stringify...`);

        const payloadSize = JSON.stringify(figuresForCsharp).length;

        const t4 = performance.now();
        sendToTerminal(`⏱️ [T+${Math.round(t4 - T0)}ms] PIPELINE 5/5 — stringify ${Math.round(t4 - t3)}ms (${(payloadSize / 1024).toFixed(0)}KB)`);

        const profileUsedForCalc = {
          ...profiles.editingProfile,
          blocksPerRow: blocksPerRow
        };

        lastCalcResponseRef.current = {
          success: true,
          data: {
            figures: figuresForCsharp,
            materials: result.materials ?? null,
            maximumY_mm: result.maximumY,
            blocksPerRow: blocksPerRow
          },
          profile: profileUsedForCalc
        };

        const tTotal = performance.now();
        sendToTerminal(`✅ [T+${Math.round(tTotal - T0)}ms] PIPELINE COMPLETO — total desde clique: ${Math.round(tTotal - T0)}ms, pipeline: ${Math.round(tTotal - tPipeline)}ms`);
      } else {
        setStatus('error');
        setError(result.error || 'Erro desconhecido no calculo');
        setProgress(0);
        setProgressLabel(null);
        sendToTerminal('[MONTATECH] Erro no calculo', { error: result.error });
      }
    } catch (err) {
      clearInterval(simTimer);
      currentCalcRequestIdRef.current = null;

      if (!isMountedRef.current) return;

      setIsRecovering(false);
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido no calculo';
      setStatus('error');
      setError(errorMsg);
      setProgress(0);
      setProgressLabel(null);
      sendToTerminal('[MONTATECH] Excecao no calculo', { error: errorMsg });
    }
  }, [curveData, profiles.editingProfile, handleProgress, resolveBoundsWarning, applyBlocksPerRow]);

  /**
   * Otimiza o resultado com N tentativas de moreCombinations
   */
  const handleOptimize = useCallback(async (level: OptimizeLevel) => {
    if (!requestIdRef.current) {
      setError('Nenhuma sessao ativa. Execute o calculo primeiro.');
      return;
    }

    const initialMaxY = maximumY;
    const maxAttempts = OPTIMIZE_ATTEMPTS[level];

    sendToTerminal(`[MONTATECH/OPTIMIZE] Iniciando ${level} (${maxAttempts} tentativas)`, {
      requestId: requestIdRef.current,
      initialMaximumY: initialMaxY
    });

    setStatus('optimizing');
    setError(null);
    setBoundsWarning(null);
    setProgress(0);
    setProgressLabel(`Otimizando... 0/${maxAttempts} tentativas`);

    try {
      const result = await optimizeNesting({
        requestId: requestIdRef.current,
        level,
        onProgress: (data) => {
          if (!isMountedRef.current) return;
          // attempt = pp (PlotCalc internal attempt number)
          setProgress(prev => Math.max(prev, data.percent));

          let label = `Otimizando... ${data.attempt}/${data.maxAttempts} tentativas`;
          if (data.improvements > 0) {
            label += ` | ${data.improvements} melhorias`;
          }
          if (data.bestMaximumY) {
            label += ` | Melhor: ${formatLength(data.bestMaximumY, getStoredDisplayUnit())}`;
          }
          setProgressLabel(label);
        }
      });

      if (!isMountedRef.current) return;

      if (result.success && result.figures) {
        setNestingResult(result.figures);
        setMaterials(result.materials || null);
        setMaximumY(result.maximumY || null);

        const nextBoundsWarning = resolveBoundsWarning(
          result.materials || null,
          result.maximumY || null,
          result.errors,
        );
        setBoundsWarning(nextBoundsWarning);
        if (nextBoundsWarning) {
          sendToTerminal('⚠️ [MONTATECH/BOUNDS] Resultado otimizado ainda acima do perfil', {
            summary: nextBoundsWarning.summary,
            details: nextBoundsWarning.details,
          });
        }

        setProgress(100);
        setStatus('ready');

        // ✅ CORREÇÃO: Atualizar resposta para apply com coordenadas locais
        // Usar applyBlocksPerRow (que pode ter sido alterado pelo usuário)
        const blocksPerRow = applyBlocksPerRow;
        const profileUsedForCalc = {
          ...profiles.editingProfile,
          blocksPerRow: blocksPerRow
        };
        
        lastCalcResponseRef.current = {
          success: true,
          data: {
            figures: buildCsharpApplyFiguresFromResult(result),
            materials: result.materials ?? null,
            maximumY_mm: result.maximumY,
            blocksPerRow: blocksPerRow
          },
          profile: profileUsedForCalc  // Guardar perfil usado na otimização
        };

        const improvement = initialMaxY && result.maximumY
          ? ((initialMaxY - result.maximumY) / initialMaxY * 100).toFixed(1)
          : null;

        const meta = result.meta;
        const attempts = meta?.attemptsDone ?? '?';
        const total = meta?.maxAttempts ?? maxAttempts;
        const melhorias = meta?.improvements ?? 0;

        if (improvement && Number(improvement) > 0) {
          const fmtY = result.maximumY ? formatLength(result.maximumY, getStoredDisplayUnit()) : '?';
          setProgressLabel(
            `Otimizado! ${fmtY} (-${improvement}%) | ${melhorias} melhoria${melhorias > 1 ? 's' : ''} em ${attempts}/${total} tentativas`
          );
        } else {
          setProgressLabel(
            `Concluído. Sem melhoria em ${attempts}/${total} tentativas.`
          );
        }

      } else {
        setStatus('ready'); // Volta ao ready, mantem resultado anterior
        setProgress(0);
        setProgressLabel(`Sem melhoria encontrada. ${result.error || ''}`);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      const errorMsg = err instanceof Error ? err.message : 'Erro na otimizacao';
      setStatus('ready'); // Volta ao ready, mantem resultado anterior
      setProgress(0);
      setProgressLabel(null);
      setError(errorMsg);

      sendToTerminal('[MONTATECH/OPTIMIZE] Excecao', { error: errorMsg });
    }
  }, [maximumY, resolveBoundsWarning, applyBlocksPerRow, profiles.editingProfile]);

  /**
   * Aplica posicoes do resultado no CorelDRAW via C# nativo
   */
  const handleApplyToCorelDraw = useCallback(async () => {
    if (!lastCalcResponseRef.current) {
      setError('Nenhum resultado de calculo para aplicar. Execute o calculo primeiro.');
      return;
    }

    const applyStartTime = Date.now();
    const figureCount = lastCalcResponseRef.current.data?.figures?.length || 0;
    sendToTerminal('🚀 [MONTATECH/APPLY] Iniciando apply a partir do preview atual', {
      figures: figureCount,
      requestId: requestIdRef.current,
      lastResultTraceId: lastResultTraceIdRef.current,
    });


    setStatus('applying');
    setError(null);
    setProgress(0);
    setProgressLabel(`Posicionando ${figureCount} objetos no CorelDRAW...`);

    // Simular progresso durante apply (C# nao envia progress updates)
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        // Incremento proporcional: ~13ms por shape, estimar tempo total
        const estimatedMs = figureCount * 5; // otimizado agora
        const elapsed = Date.now() - applyStartTime;
        const estimated = Math.min((elapsed / estimatedMs) * 100, 90);
        return Math.max(prev, estimated);
      });
    }, 100);

    try {
      // ✅ Usar o perfil guardado no cálculo, não o perfil atual (pode ter mudado)
      const profileForApply = lastCalcResponseRef.current?.profile || profiles.editingProfile;
      const calcData = lastCalcResponseRef.current;
      const figuresForApply = buildCsharpApplyFiguresFromResult({
        figures: calcData?.data?.figures,
        materials: calcData?.data?.materials ?? materials,
      });
      const blockHistogram = figuresForApply.reduce<Record<number, number>>((acc, fig) => {
        const block = fig.materialIndex ?? 0;
        acc[block] = (acc[block] ?? 0) + 1;
        return acc;
      }, {});
      sendToTerminal('📊 [MONTATECH/APPLY] Distribuição por materialIndex', blockHistogram);

      const applyPayload = {
        ...calcData,
        data: {
          ...calcData.data,
          figures: figuresForApply,
        },
      };

      const result = await applyNestingNative(applyPayload, profileForApply);
      clearInterval(progressTimer);
      const elapsed = Date.now() - applyStartTime;

      if (!isMountedRef.current) return;

      if (result.success) {
        setProgress(100);
        setProgressLabel(`${result.figuresApplied} objetos posicionados!`);
        setStatus('ready');

        if (result.figuresFailed && result.figuresFailed > 0) {
          sendToTerminal('[MONTATECH/APPLY] AVISO: shapes falharam', {
            failed: result.figuresFailed,
            applied: result.figuresApplied
          });
        }

        // Fechar MontaTech após apply com sucesso
        if (options?.onApplySuccess) {
          options.onApplySuccess();
        }
      } else {
        setProgress(0);
        setProgressLabel(null);
        setStatus('error');
        setError(result.error || 'Erro ao aplicar posicoes');

        sendToTerminal('[MONTATECH/APPLY] FALHA AO APLICAR', {
          error: result.error,
          elapsedMs: elapsed
        });
      }
    } catch (err) {
      clearInterval(progressTimer);
      const elapsed = Date.now() - applyStartTime;
      if (!isMountedRef.current) return;

      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      setProgress(0);
      setProgressLabel(null);
      setStatus('error');
      setError(errorMsg);

      sendToTerminal('[MONTATECH/APPLY] EXCECAO AO APLICAR', {
        error: errorMsg,
        elapsedMs: elapsed
      });
    }
  }, [materials, profiles.editingProfile]);

  /**
   * Cancela o calculo em andamento
   */
  const handleCancelCalculation = useCallback(async () => {
    // Cancel optimize if optimizing
    if (status === 'optimizing' && requestIdRef.current) {
      sendToTerminal('[MONTATECH] Cancelando otimizacao...', {
        requestId: requestIdRef.current
      });
      cancelOptimize(requestIdRef.current);
      if (isMountedRef.current) {
        setStatus('ready');
        setProgress(0);
        setProgressLabel(null);
        setError('Otimizacao cancelada pelo usuario');
      }
      return;
    }

    // Cancel calculation
    if (!currentCalcRequestIdRef.current) {
      return;
    }

    sendToTerminal('[MONTATECH] Cancelando calculo...', {
      requestId: currentCalcRequestIdRef.current
    });

    try {
      await stopCalculationApi(currentCalcRequestIdRef.current);

      if (isMountedRef.current) {
        setStatus('ready');
        setProgress(0);
        setProgressLabel(null);
        setIsRecovering(false);
        setError('Calculo cancelado pelo usuario');
        currentCalcRequestIdRef.current = null;
      }

    } catch (err) {
    }
  }, [status]);

  /**
   * Reseta todo o estado
   */
  const handleReset = useCallback(() => {
    if (currentCalcRequestIdRef.current) {
      stopCalculationApi(currentCalcRequestIdRef.current).catch(() => {});
      currentCalcRequestIdRef.current = null;
    }

    if (requestIdRef.current) {
      closeSession(requestIdRef.current).catch(() => {});
      requestIdRef.current = null;
    }

    setStatus('idle');
    setError(null);
    setProgress(0);
    setProgressLabel(null);
    setIsRecovering(false);
    setEmptyState(null);
    setCurveData(null);
    setNestingResult(null);
    setMaterials(null);
    setMaximumY(null);
    setBoundsWarning(null);
    lastCalcResponseRef.current = null;
    canvas.resetTransform();

  }, [canvas]);

  // ==================== COMPUTED ====================

  const isProcessing = status === 'collecting' || status === 'calculating' || status === 'applying' || status === 'optimizing';
  const canCollect = !isProcessing;
  const canCalculate = !isProcessing && !!curveData && curveData.count > 0;
  const canOptimize = !isProcessing && !!nestingResult && !!requestIdRef.current;
  const canApply = !isProcessing && !!nestingResult && !!lastCalcResponseRef.current;
  const canCancel = (status === 'calculating' && !!currentCalcRequestIdRef.current) || status === 'optimizing';

  // ==================== EFFECTS ====================

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      if (currentCalcRequestIdRef.current) {
        stopCalculationApi(currentCalcRequestIdRef.current).catch(() => {});
      }

      if (requestIdRef.current) {
        closeSession(requestIdRef.current).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (autoCollectStartedRef.current) {
      return;
    }

    autoCollectStartedRef.current = true;
    void handleCollectShapes('auto');
  }, [handleCollectShapes]);

  return {
    // Status
    status,
    error,
    progress,
    progressLabel,
    isRecovering,
    emptyState,

    // Data
    curveData,
    nestingResult,
    maximumY,
    boundsWarning,
    toolVersion: null, // C# nativo nao precisa de toolData

    // Canvas
    containerRef,
    canvas,

    // Profiles
    profiles,

    // Settings Modal
    showSettings,
    settingsTab,
    setShowSettings,
    setSettingsTab,
    
    // ✅ Layout de Grid (pode ser alterado antes de aplicar)
    applyBlocksPerRow,
    setApplyBlocksPerRow,

    // Actions
    collectShapes: () => handleCollectShapes('manual'),
    calculateNesting: handleCalculateNesting,
    optimizeResult: handleOptimize,
    applyToCorelDraw: handleApplyToCorelDraw,
    cancelCalculation: handleCancelCalculation,
    reset: handleReset,

    // Computed
    isProcessing,
    canCollect,
    canCalculate,
    canOptimize,
    canApply,
    canCancel
  };
}
